import type { SxProps, Theme } from "@mui/material";
import type { Agreement } from "../../../clients/graphqlTypes.js";
import { color } from "../../../common/MuiThemeProvider.js";
import { isItemGreyedOut } from "../doc-items/helpers.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";

// ---------------------------------------------------------------------------
// Row class computation — consumed by DocDataGrid getItemRowClassName prop
// ---------------------------------------------------------------------------

export function getDocDataGridItemRowClassName(
  item: DocDataGrid_ItemFragment,
  docAgreement: Agreement | undefined,
  isMuted: boolean
): string {
  const classes: string[] = [];
  if (isItemGreyedOut(item)) classes.push("dg-row--greyed-out");
  if (item.decisionOptionElimination || item.isItemEliminated)
    classes.push("dg-row--decision-eliminated");
  if (isMuted) classes.push("dg-row--muted");
  if (item.deletedAt) classes.push("dg-row--deleted");
  if (item.type === "defect") classes.push("dg-row--defect");
  if (item.type === "unpaid") classes.push("dg-row--unpaid");
  if (docAgreement && docAgreement !== "NONE" && item.needsAgreement)
    classes.push("dg-row--needs-agreement");
  if (item.type === "paid") {
    if (item.binding === "draft") classes.push("dg-row--paid-draft");
    if (item.binding === "non_binding")
      classes.push("dg-row--paid-non-binding");
  }
  return classes.join(" ");
}

// ---------------------------------------------------------------------------
// DataGrid sx — defines styles for all row classes set by getItemRowClassName
// ---------------------------------------------------------------------------

export const DOC_DATA_GRID_ROW_SX: SxProps<Theme> = (theme: Theme) => ({
  // Background / opacity / text colour per item state
  "& .MuiDataGrid-row.dg-row--greyed-out": {
    backgroundColor: theme.palette.grey[100],
  },
  "& .MuiDataGrid-row.dg-row--decision-eliminated": {
    color: theme.palette.grey[600],
  },
  "& .MuiDataGrid-row.dg-row--muted": { opacity: 0.38 },
  "& .MuiDataGrid-row.dg-row--deleted": { opacity: 0.4 },
  "& .MuiDataGrid-row.dg-row--deleted .msys-tree-title": {
    textDecoration: "line-through",
  },
  "& .MuiDataGrid-row.dg-row--defect": { color: color.red },
  "& .MuiDataGrid-row.dg-row--unpaid": { color: color.turquoise },
  "& .MuiDataGrid-row.dg-row--paid-draft": { opacity: 0.38 },
  "& .MuiDataGrid-row.dg-row--paid-draft .msys-tree-title": {
    fontStyle: "italic",
  },
  "& .MuiDataGrid-row.dg-row--paid-non-binding .msys-tree-title": {
    fontStyle: "italic",
  },

  // Colored 4px left border via ::before pseudo-element on the row element
  "& .MuiDataGrid-row.dg-row--defect::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
    backgroundColor: color.red,
  },
  "& .MuiDataGrid-row.dg-row--unpaid::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
    backgroundColor: color.turquoise,
  },
  "& .MuiDataGrid-row.dg-row--needs-agreement::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
    backgroundColor: theme.palette.warning.main,
  },

  // selected color overwrite with higher specificity
  "& .MuiDataGrid-row.Mui-selected.dg-row--selected": {
    backgroundColor:
      "color-mix(in srgb, var(--DataGrid-t-color-background-base), var(--DataGrid-t-color-interactive-selected) calc(var(--DataGrid-t-color-interactive-selected-opacity) * 100%))",
  },
});
